import { useMemo, useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import type Meeting from "@acto/taskpane/core/domain/Meeting";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import {
  useNearbyRankedClients,
  type NearbySuggestionItem,
} from "@acto/taskpane/field-sales-2/data-access/useNearbyRankedClients";
import { SearchIcon } from "@acto/web-common/components";
import { FullScreenSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { CtaRow } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";
import { EmptyState, EmptyTitle } from "@acto/taskpane/field-sales-2/components/primitives/EmptyState";
import { OutlinedPillButton } from "@acto/taskpane/field-sales-2/components/primitives/OutlinedPillButton";
import { BottomNavActionPill } from "@acto/taskpane/field-sales-2/components/patterns/BottomNav/BottomNavActionPill";
import { CustomerSection } from "@acto/taskpane/field-sales-2/components/patterns/CustomerSection";
import { useDateFormatter } from "@acto/taskpane/field-sales-2/utils/hooks/useDateFormatter";
import { formatCustomerAddress } from "@acto/taskpane/field-sales-2/utils/format";
import { pickFocusedMeeting } from "@acto/taskpane/field-sales-2/utils/meetingFocus";
import { ReferenceMeetingPicker } from "./ReferenceMeetingPicker";
import { hasNearbyCardMeta, NearbyCardMeta } from "./NearbyCardMeta";
import { PeriodComparisonScope } from "@acto/taskpane/field-sales-2/components/patterns/PeriodComparisonScope/PeriodComparisonScope";
import { YEAR_TO_DATE_SELECTION } from "@acto/taskpane/field-sales-2/utils/periodSelection";

const Content = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  // Fills the sheet body so the CtaRow's `marginTop: auto` pins the search pill to the
  // bottom even when the suggestion list is short.
  flex: 1,
}));

const PickerSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  margin: `0 ${theme.fieldSalesTwo.spacing[12]}`,
  padding: theme.fieldSalesTwo.spacing[16],
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
}));

const PickerHeading = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
  padding: `${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[4]} 0`,
}));

const PickerTitle = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const PickerHelper = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const SectionTitle = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[4],
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[16]}`,
  textAlign: "center",
}));

const SectionHeading = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.familySecondary,
  fontSize: theme.fieldSalesTwo.font.size[20],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.5,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Anchor = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.tight,
  color: theme.fieldSalesTwo.colors.neutral[700],
  whiteSpace: "pre-line",
}));

const Notice = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
  margin: `0 ${theme.fieldSalesTwo.spacing[12]}`,
  padding: theme.fieldSalesTwo.spacing[16],
  borderRadius: theme.fieldSalesTwo.radius[24],
  border: `${theme.strokeWidth.thin} dashed ${theme.fieldSalesTwo.colors.neutral[300]}`,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const NoticeTitle = styled("span")(({ theme }) => ({
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const RetryButton = styled(OutlinedPillButton)({
  alignSelf: "center",
});

const toAnchorText = (customer: Customer): string => {
  const address = formatCustomerAddress(customer);
  return address ? `${customer.name}\n${address}` : customer.name;
};

interface NearbySuggestionsProps {
  /** The selected day's appointments — the reference-point candidates. */
  dayMeetings: Meeting[];
  onPlan: (suggestion: NearbySuggestionItem) => void;
  onOpenCustomer: (customer: Customer) => void;
  /** The plain customer search, for booking a client that is not on the list. */
  onSearch: () => void;
}

interface NearbyCustomersSheetProps extends NearbySuggestionsProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  day: Date | null;
}

export function NearbyCustomersSheet({ isOpen, onOpenChange, day, ...bodyProps }: NearbyCustomersSheetProps) {
  const t = useTranslations("fieldSalesTwo.nearby");
  const { formatWeekdayShortDayMonth } = useDateFormatter();

  return (
    <FullScreenSheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("sheetTitle")}
      subtitle={day ? formatWeekdayShortDayMonth(day) : undefined}
      closeAriaLabel={t("closeAriaLabel")}
    >
      <NearbySuggestions {...bodyProps} />
    </FullScreenSheet>
  );
}

// The sheet body mounts fresh on every open (Radix unmounts the dialog content on close), so the
// reference is seeded once per opening: a day gaining a meeting while the sheet is open (the rep
// just booked one) must not silently move the reference point and re-rank the list.
function NearbySuggestions({ dayMeetings, onPlan, onOpenCustomer, onSearch }: NearbySuggestionsProps) {
  const t = useTranslations("fieldSalesTwo.nearby");
  const track = useTrack();

  const [referenceMeetingId, setReferenceMeetingId] = useState(() => pickFocusedMeeting(dayMeetings)?.id ?? null);

  const referenceMeeting = useMemo(
    () => dayMeetings.find((meeting) => meeting.id === referenceMeetingId),
    [dayMeetings, referenceMeetingId],
  );

  const { suggestions, revenuePeriod, originPrecision, isPending, error, refetch } = useNearbyRankedClients(
    referenceMeeting?.customer.id ?? null,
  );

  // The list is ranked relative to the reference, so a pending re-rank must render skeletons,
  // never the previous reference's rows.
  const visibleSuggestions = isPending ? undefined : suggestions;

  const customers = useMemo(() => visibleSuggestions?.map((suggestion) => suggestion.customer), [visibleSuggestions]);
  const proximityByCustomerId = useMemo(
    () => new Map(visibleSuggestions?.map((suggestion) => [suggestion.customer.id, suggestion.proximity]) ?? []),
    [visibleSuggestions],
  );

  // An empty focus list also comes back with a null origin precision; only a populated list
  // without one means the reference point itself has no usable address.
  const originUnusable = visibleSuggestions != null && visibleSuggestions.length > 0 && originPrecision == null;

  const selectReference = (meeting: Meeting) => {
    setReferenceMeetingId(meeting.id);
    track(AnalyticsEvents.NEARBY_REFERENCE_CHANGED, { meetingId: meeting.id, customerId: meeting.customer.id });
  };

  const planSuggestion = (customer: Customer) => {
    const suggestion = visibleSuggestions?.find((candidate) => candidate.customer.id === customer.id);
    if (!suggestion) return;
    track(AnalyticsEvents.NEARBY_SUGGESTION_PLANNED, {
      customerId: suggestion.customer.id,
      precision: suggestion.proximity?.precision ?? null,
    });
    onPlan(suggestion);
  };

  const openSearch = () => {
    track(AnalyticsEvents.NEARBY_SEARCH_OPENED, {
      suggestionCount: error ? null : (visibleSuggestions?.length ?? null),
    });
    onSearch();
  };

  return (
    <PeriodComparisonScope selection={YEAR_TO_DATE_SELECTION}>
      <Content>
        <PickerSection>
          <PickerHeading>
            <PickerTitle>{t("listHeader")}</PickerTitle>
            <PickerHelper>{t("listHelper")}</PickerHelper>
          </PickerHeading>
          {referenceMeetingId && (
            <ReferenceMeetingPicker
              meetings={dayMeetings}
              selectedMeetingId={referenceMeetingId}
              onSelect={selectReference}
            />
          )}
        </PickerSection>

        <SectionTitle>
          <SectionHeading>{t("suggestionsTitle")}</SectionHeading>
          {referenceMeeting && <Anchor>{toAnchorText(referenceMeeting.customer)}</Anchor>}
        </SectionTitle>

        {originUnusable && (
          <Notice>
            <NoticeTitle>{t("originUnusableTitle")}</NoticeTitle>
            <span>{t("originUnusableHint")}</span>
          </Notice>
        )}

        {error ? (
          <EmptyState>
            <EmptyTitle>{t("error")}</EmptyTitle>
            <RetryButton type="button" onClick={refetch}>
              {t("retry")}
            </RetryButton>
          </EmptyState>
        ) : (
          <CustomerSection
            customers={customers}
            revenuePeriod={revenuePeriod}
            isLoading={isPending}
            onOpenCustomer={onOpenCustomer}
            primaryAction={(customer) => ({ label: t("planButton"), onClick: () => planSuggestion(customer) })}
            headerMeta={(customer) => {
              const proximity = proximityByCustomerId.get(customer.id) ?? null;
              return hasNearbyCardMeta(customer, proximity) ? (
                <NearbyCardMeta customer={customer} proximity={proximity} />
              ) : null;
            }}
            activityMeta={null}
            emptyText={t("empty")}
          />
        )}

        <CtaRow>
          <BottomNavActionPill onClick={openSearch}>
            <SearchIcon aria-hidden="true" />
            {t("searchButton")}
          </BottomNavActionPill>
        </CtaRow>
      </Content>
    </PeriodComparisonScope>
  );
}
